import { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { api } from '../api/client';
import { ApiError } from '../api/types';
import { theme } from '../theme';
import { readInviteToken, withoutInviteParam } from '../utils/invite';

/**
 * Полноэкранный вход в семейный бюджет («Антверпен»): логин или первичная
 * регистрация. Регистрация возможна двумя путями — email из семейного allowlist
 * или ссылка-приглашение `?invite=…`; во втором случае токен уходит на сервер, и
 * household определяется им, а не тем, что мы напишем в теле запроса.
 * Cookie ставит сервер — при успехе просто зовём onSuccess().
 */
export default function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  // Токен читаем один раз при монтировании: дальше адрес может измениться
  // (мы же сами его и чистим после успешной регистрации).
  const [invite] = useState<string | null>(() => readInviteToken(window.location.search));
  const [mode, setMode] = useState<'login' | 'register'>(invite ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const canSubmit = !busy && email.trim() !== '' && password !== '';

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      if (mode === 'login') {
        await api.login(email.trim(), password);
      } else {
        await api.registerUser(email.trim(), password, name.trim() || undefined, invite ?? undefined);
        // Токен погашен — оставить его в адресе значит при следующей перезагрузке
        // снова показать «вход по приглашению» с уже мёртвой ссылкой.
        if (invite) {
          const { pathname, search, hash } = window.location;
          window.history.replaceState(null, '', pathname + withoutInviteParam(search) + hash);
        }
      }
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_invite') {
          setErrorMsg('Приглашение не действует — ссылку уже использовали или срок вышел');
        } else {
          setErrorMsg(err.code === 'network' ? 'Нет сети' : err.message);
        }
      } else {
        setErrorMsg('Что-то пошло не так');
      }
    } finally {
      setBusy(false);
    }
  }

  const register = mode === 'register';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.bg,
        color: theme.colors.ink,
        fontFamily: theme.font,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px calc(24px + env(safe-area-inset-bottom))',
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: '100%',
          maxWidth: 360,
          background: theme.colors.card,
          border: `1px solid ${theme.colors.line}`,
          borderRadius: theme.radius.lg,
          padding: '28px 22px 24px',
        }}
      >
        <h1 style={{ margin: '0 0 4px', fontSize: 30 }}>
          {register ? (invite ? 'По приглашению' : 'Первый вход') : 'Вход'}
        </h1>
        <div style={{ color: theme.colors.sub, fontSize: 14, marginBottom: invite && register ? 12 : 20 }}>
          {register
            ? invite
              ? 'Придумайте пароль — бюджет уже ждёт'
              : 'Создайте пароль для семейного бюджета'
            : 'Семейный бюджет'}
        </div>
        {invite && register && (
          <div
            style={{
              color: theme.colors.sub,
              fontSize: 12,
              lineHeight: 1.6,
              marginBottom: 20,
              paddingLeft: 10,
              borderLeft: `1px solid ${theme.colors.line}`,
            }}
          >
            Ссылка одноразовая: после регистрации она погаснет.
          </div>
        )}

        <label style={labelStyle}>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="you@example.com"
            style={inputStyle}
          />
        </label>

        {register && (
          <label style={labelStyle}>
            Имя <span style={{ color: theme.colors.sub }}>(необязательно)</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              placeholder="Как к вам обращаться"
              style={inputStyle}
            />
          </label>
        )}

        <label style={labelStyle}>
          Пароль
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={register ? 'new-password' : 'current-password'}
            placeholder={register ? 'Придумайте пароль' : 'Пароль'}
            style={inputStyle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
          />
        </label>
        {register && (
          <div style={{ color: theme.colors.sub, fontSize: 13, margin: '-6px 0 14px' }}>
            Пароль от 8 символов
          </div>
        )}

        {errorMsg && (
          <div style={{ color: theme.colors.red, fontSize: 14, marginBottom: 14 }}>
            {errorMsg}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            width: '100%',
            padding: '13px 0',
            background: canSubmit ? theme.colors.ink : theme.colors.line,
            color: canSubmit ? '#E4E1D8' : theme.colors.sub,
            border: 'none',
            borderRadius: theme.radius.sm,
            fontFamily: theme.fontDisplay,
            fontSize: 17,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {busy ? 'Секунду…' : register ? 'Создать и войти' : 'Войти'}
        </button>

        <button
          type="button"
          onClick={() => {
            setMode(register ? 'login' : 'register');
            setErrorMsg(null);
          }}
          style={{
            display: 'block',
            margin: '16px auto 0',
            background: 'transparent',
            border: 'none',
            color: theme.colors.sub,
            fontFamily: theme.font,
            fontSize: 14,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {register ? 'Уже есть пароль? Войти' : 'Первый вход? Создать пароль'}
        </button>
      </form>
    </div>
  );
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  color: theme.colors.sub,
  marginBottom: 14,
};

const inputStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 5,
  padding: '11px 12px',
  background: theme.colors.bg,
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm,
  fontFamily: theme.font,
  fontSize: 16,
  color: theme.colors.ink,
  outline: 'none',
};
